/**
 * The experiments client: the website asks messenger which variant each visitor gets
 * (`POST /experiments/assignments`), shows it, and reports what the visitor saw (exposures) and did (events).
 *
 * Any doubt means the default experience: before the first answer, after a failed request, with storage blocked or
 * an experiment that is not running, `enter()` returns null (or the control) and nothing is logged. No method ever
 * throws into the page.
 *
 * A returning visitor's last answer is read from storage synchronously, so their variant is known on the first
 * render (no flicker); the server is asked again in the background.
 */
import { readOverrides } from './overrides.js';
import { SendQueue } from './queue.js';
import { SafeStorage, defaultStorage } from './storage.js';
const CACHE_KEY = 'oi_exp_assignments';
const IDENTIFIED_KEY = 'oi_exp_identified';
const SCHEMA = 1;
const DEFAULT_TIMEOUT_MS = 3_000;
const DEFAULT_MAX_STALE_MS = 24 * 60 * 60 * 1_000;
const DEFAULT_FLUSH_DELAY_MS = 2_000;
const MAX_CONTEXT_KEYS = 20;
const MAX_STRING = 256;
const EVENT_NAME = /^[a-z][a-z0-9_]{0,63}$/;
function uuid() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
            return crypto.randomUUID();
    }
    catch {
        // fall through
    }
    const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}
/** At most 20 short scalars: what the server accepts as exposure context, event properties and attributes. */
function scalars(input) {
    const out = {};
    if (!input)
        return out;
    for (const [key, value] of Object.entries(input).slice(0, MAX_CONTEXT_KEYS)) {
        if (key.length > 64)
            continue;
        if (typeof value === 'string')
            out[key] = value.slice(0, MAX_STRING);
        else if (value === null || typeof value === 'number' || typeof value === 'boolean')
            out[key] = value;
    }
    return out;
}
export class Experiments {
    options;
    storage;
    fetchImpl;
    baseUrl;
    platform;
    now;
    listeners = new Set();
    exposed = new Set();
    exposures;
    events;
    overrides;
    body = null;
    source = 'none';
    lastError = null;
    loading = null;
    pendingTracks = [];
    isReady = false;
    constructor(options) {
        this.options = options;
        this.storage = new SafeStorage(options.storage === undefined ? defaultStorage() : options.storage);
        this.fetchImpl = options.fetch ?? ((...args) => globalThis.fetch(...args));
        this.baseUrl = options.baseUrl.replace(/\/+$/, '');
        this.platform = options.platform ?? 'web';
        this.now = options.now ?? (() => Date.now());
        const sleep = options.sleep ?? ((ms) => new Promise(resolve => setTimeout(resolve, ms)));
        const delayMs = options.flushDelayMs ?? DEFAULT_FLUSH_DELAY_MS;
        const onDrop = (what) => (reason, count) => this.warn(`${count} ${what} ${reason}`);
        this.exposures = new SendQueue({
            url: `${this.baseUrl}/experiments/exposures`,
            body: events => ({ schema: SCHEMA, platform: this.platform, events }),
            fetch: this.fetchImpl,
            delayMs,
            sleep,
            onDrop: onDrop('exposures'),
        });
        this.events = new SendQueue({
            url: `${this.baseUrl}/experiments/events`,
            body: events => ({ schema: SCHEMA, platform: this.platform, events }),
            fetch: this.fetchImpl,
            delayMs,
            sleep,
            onDrop: onDrop('events'),
        });
        this.overrides = this.safe(() => readOverrides(this.storage, this.search()), {
            off: false,
            variants: {},
        });
        this.safe(() => this.useCache(), undefined);
        this.safe(() => this.listenForPageHide(), undefined);
        this.loading = this.load();
    }
    ready() {
        return this.loading ?? Promise.resolve();
    }
    get(key) {
        return this.safe(() => this.assignment(key), {
            key,
            variant: null,
            enrolled: false,
            forced: false,
            payload: {},
        });
    }
    enter(key, options = {}) {
        return this.safe(() => {
            const assignment = this.assignment(key);
            const wire = this.body?.assignments[key];
            if (assignment.variant == null || !wire)
                return assignment.variant;
            if (assignment.enrolled && wire.log_exposures && !this.exposed.has(key)) {
                const subject = this.subjectFor(wire);
                if (subject) {
                    this.exposed.add(key);
                    this.exposures.push({
                        event_id: uuid(),
                        key,
                        variant: assignment.variant,
                        unit: wire.unit,
                        subject_id: subject,
                        config_version: this.body?.config_version ?? 0,
                        forced: assignment.forced,
                        ...(options.entryPoint ? { entry_point: options.entryPoint.slice(0, 64) } : {}),
                        context: scalars(options.context),
                        client_ts: new Date(this.now()).toISOString(),
                    });
                    try {
                        this.options.onExposure?.(key, assignment.variant, options);
                    }
                    catch {
                        // the analytics bridge must never break the page
                    }
                }
            }
            return assignment.variant;
        }, null);
    }
    track(name, properties) {
        this.safe(() => {
            if (!EVENT_NAME.test(name))
                return this.warn(`track("${name}"): event names are lower snake case`);
            const entry = {
                name,
                properties: scalars(properties),
                at: new Date(this.now()).toISOString(),
            };
            if (!this.isReady) {
                this.pendingTracks.push(entry);
                return;
            }
            this.sendTrack(entry);
        }, undefined);
    }
    async identify() {
        try {
            const subjectId = this.options.getSubjectId();
            const uid = this.options.getUid?.() ?? null;
            const token = await this.options.getIdToken?.();
            if (!subjectId || !uid || !token)
                return;
            const marker = `${subjectId}:${uid}`;
            if (this.storage.get(IDENTIFIED_KEY) === marker)
                return;
            const response = await this.fetchImpl(`${this.baseUrl}/experiments/identify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ subject_id: subjectId }),
            });
            if (response.ok)
                this.storage.set(IDENTIFIED_KEY, marker);
        }
        catch (error) {
            this.warn(`identify failed: ${String(error)}`);
        }
    }
    refresh() {
        this.loading = this.load();
        return this.loading;
    }
    async flush() {
        await Promise.all([this.exposures.flush(), this.events.flush()]);
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    debug() {
        return {
            ready: this.isReady,
            source: this.source,
            configVersion: this.body?.config_version ?? null,
            subjectId: this.safe(() => this.options.getSubjectId(), null),
            assignments: { ...(this.body?.assignments ?? {}) },
            overrides: this.overrides.off ? 'off' : { ...this.overrides.variants },
            exposed: [...this.exposed],
            queued: { exposures: this.exposures.size, events: this.events.size },
            lastError: this.lastError,
        };
    }
    // ── internals ──────────────────────────────────────────────────────────────
    assignment(key) {
        const wire = this.body?.assignments[key];
        if (!wire || this.overrides.off)
            return { key, variant: null, enrolled: false, forced: false, payload: {} };
        const forced = this.overrides.variants[key];
        if (forced)
            return { key, variant: forced, enrolled: true, forced: true, payload: wire.payload ?? {} };
        return {
            key,
            variant: wire.variant,
            enrolled: wire.enrolled,
            forced: wire.forced,
            payload: wire.payload ?? {},
        };
    }
    subjectFor(wire) {
        if (wire.unit === 'user')
            return this.options.getUid?.() ?? null;
        return this.options.getSubjectId();
    }
    attributes() {
        return scalars(this.safe(() => this.options.getAttributes?.() ?? {}, {}));
    }
    identity() {
        return {
            subjectId: this.safe(() => this.options.getSubjectId(), null),
            uid: this.safe(() => this.options.getUid?.() ?? null, null),
            attributes: this.attributes(),
        };
    }
    useCache() {
        const { subjectId, uid, attributes } = this.identity();
        const cached = this.storage.getJson(CACHE_KEY);
        const maxStale = this.options.maxStaleMs ?? DEFAULT_MAX_STALE_MS;
        if (cached &&
            subjectId &&
            cached.subjectId === subjectId &&
            cached.uid === uid &&
            cached.attributes === JSON.stringify(attributes) &&
            this.now() - cached.fetchedAt < maxStale &&
            cached.body?.assignments) {
            this.body = cached.body;
            this.source = 'cache';
        }
    }
    async load() {
        const { subjectId, uid, attributes } = this.identity();
        if (!subjectId) {
            this.markReady();
            return;
        }
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = setTimeout(() => controller?.abort(), this.options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
        try {
            const response = await this.fetchImpl(`${this.baseUrl}/experiments/assignments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    schema: SCHEMA,
                    platform: this.platform,
                    subject_id: subjectId,
                    uid,
                    attributes,
                }),
                ...(controller ? { signal: controller.signal } : {}),
            });
            if (!response.ok)
                throw new Error(`assignments answered ${response.status}`);
            const body = (await response.json());
            if (!body || typeof body.assignments !== 'object' || body.schema > SCHEMA) {
                throw new Error('assignments answer not understood');
            }
            this.body = body;
            this.source = 'network';
            this.lastError = null;
            this.storage.setJson(CACHE_KEY, {
                subjectId,
                uid,
                attributes: JSON.stringify(attributes),
                fetchedAt: this.now(),
                body,
            });
            this.notify();
        }
        catch (error) {
            // Fail static: keep a usable cached answer, else everyone gets the default experience.
            this.lastError = String(error);
            this.warn(`assignments: ${this.lastError}`);
        }
        finally {
            clearTimeout(timer);
            this.markReady();
        }
    }
    markReady() {
        const first = !this.isReady;
        this.isReady = true;
        if (first) {
            const pending = this.pendingTracks;
            this.pendingTracks = [];
            pending.forEach(entry => this.sendTrack(entry));
            this.notify();
        }
    }
    sendTrack(entry) {
        if (!this.body?.track_events.includes(entry.name))
            return; // no running experiment measures it
        const subjectId = this.options.getSubjectId();
        if (!subjectId)
            return;
        const uid = this.options.getUid?.() ?? null;
        this.events.push({
            event_id: uuid(),
            name: entry.name,
            subject_id: subjectId,
            ...(uid ? { uid } : {}),
            properties: entry.properties,
            client_ts: entry.at,
        });
    }
    notify() {
        for (const listener of [...this.listeners]) {
            try {
                listener();
            }
            catch {
                // a listener's error is its own
            }
        }
    }
    listenForPageHide() {
        if (typeof document === 'undefined' || typeof window === 'undefined')
            return;
        const flush = () => void this.flush();
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden')
                flush();
        });
        window.addEventListener('pagehide', flush);
        window.__oiExp = () => this.debug();
    }
    search() {
        if (this.options.getSearch)
            return this.options.getSearch();
        return typeof window !== 'undefined' ? window.location.search : '';
    }
    warn(message) {
        if (this.options.debug && typeof console !== 'undefined')
            console.warn(`[experiments] ${message}`);
    }
    safe(work, fallback) {
        try {
            return work();
        }
        catch (error) {
            this.lastError = String(error);
            this.warn(String(error));
            return fallback;
        }
    }
}
/** Creates the one client a page uses (create it once, at the app root). */
export function createExperiments(options) {
    return new Experiments(options);
}
